import { financialDeckModel } from "@/models/financialDecks";
import { arrayRemove, arrayUnion, setDoc } from "firebase/firestore";
import { useAtomValue } from "jotai";
import { useState } from "react";

export const useModelSharingAllowedEmails = (modelId: string) => {
  const deckEntity = financialDeckModel.createEntityInstance({ id: modelId });
  const {
    data: model,
    loading,
    error,
  } = useAtomValue(deckEntity.getObserveEntityRecordAtom());

  const allowedEmails = (model?.allowedEmails || []).filter(
    (email: string) => email !== "public",
  ) as string[];

  return [allowedEmails, loading, error] as const;
};

export const useModelSharingIsPublic = (modelId: string) => {
  const deckEntity = financialDeckModel.createEntityInstance({ id: modelId });
  const {
    data: model,
    loading,
    error,
  } = useAtomValue(deckEntity.getObserveEntityRecordAtom());

  const isPublic = model?.allowedEmails?.includes("public") ?? false;

  return [isPublic, loading, error] as const;
};

export const useModelSharingTogglePublic = (modelId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const modelRef = financialDeckModel.createEntityInstance({
    id: modelId,
  }).docRef;
  const [isPublic] = useModelSharingIsPublic(modelId);

  const setPublic = async () => {
    setLoading(true);

    try {
      if (isPublic) {
        setDoc(
          modelRef,
          {
            allowedEmails: arrayRemove("public"),
          },
          { merge: true },
        );
      } else {
        setDoc(
          modelRef,
          {
            allowedEmails: arrayUnion("public"),
          },
          { merge: true },
        );
      }
    } catch (error: unknown) {
      setError(error as Error);
    }
    setLoading(false);
  };

  return [setPublic, loading, error] as const;
};

export const useModelSharingAddEmail = (modelId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const modelRef = financialDeckModel.createEntityInstance({
    id: modelId,
  }).docRef;

  const addEmail = async (email: string) => {
    setLoading(true);
    try {
      setDoc(
        modelRef,
        {
          allowedEmails: arrayUnion(email),
        },
        { merge: true },
      );
    } catch (error: unknown) {
      setError(error as Error);
    }
    setLoading(false);
  };

  return [addEmail, loading, error] as const;
};

export const useModelSharingRemoveEmail = (modelId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const modelRef = financialDeckModel.createEntityInstance({
    id: modelId,
  }).docRef;

  const removeEmail = async (email: string) => {
    setLoading(true);
    try {
      setDoc(
        modelRef,
        {
          allowedEmails: arrayRemove(email),
        },
        { merge: true },
      );
    } catch (error: unknown) {
      setError(error as Error);
    }
    setLoading(false);
  };

  return [removeEmail, loading, error] as const;
};
